import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  FormControlLabel,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import { providersService } from '../../shared/services/providers.service';
import { useApiError } from '../../shared/errors/useApiError';
import { useNotification } from '../../shared/notifications/NotificationProvider';

/** Mirrors the backend's @Min/@Max on travelRadiusKm (V48). */
const MIN_RADIUS = 1;
const MAX_RADIUS = 2000;

/**
 * Dashboard card letting a provider opt into out-of-city jobs (V48 service
 * coverage). When enabled with a radius, the provider surfaces in searches
 * for any city within that distance of their base city, flagged as a
 * traveller. Self-contained: owns its edit state and the updateMine call,
 * mirroring the OpeningHoursCard / EventSpecializationsCard pattern.
 */
export function TravelCoverageCard({
  canTravel: initialCanTravel,
  travelRadiusKm: initialRadius,
  cityName,
}: {
  canTravel: boolean;
  travelRadiusKm: number | null | undefined;
  cityName: string;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { notify } = useNotification();
  const { translate } = useApiError();

  const [canTravel, setCanTravel] = useState(initialCanTravel);
  const [radius, setRadius] = useState(initialRadius != null ? String(initialRadius) : '');

  // Re-sync local edit state whenever the provider query refetches.
  useEffect(() => {
    setCanTravel(initialCanTravel);
    setRadius(initialRadius != null ? String(initialRadius) : '');
  }, [initialCanTravel, initialRadius]);

  const radiusNum = radius.trim() === '' ? null : Number(radius);
  const radiusValid =
    !canTravel
    || (radiusNum != null
        && Number.isFinite(radiusNum)
        && radiusNum >= MIN_RADIUS
        && radiusNum <= MAX_RADIUS);
  const isUnchanged =
    canTravel === initialCanTravel && (radiusNum ?? null) === (initialRadius ?? null);

  const save = useMutation({
    mutationFn: () =>
      providersService.updateMine({
        canTravel,
        // Only push a radius when travelling is on; the button is disabled
        // until a valid one is entered, so radiusNum is set here.
        travelRadiusKm: canTravel ? radiusNum ?? undefined : undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['provider', 'me'] });
      notify.success(t('common.save'));
    },
    onError: (err) => notify.error(translate(err)),
  });

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        mb: 4,
        mt: 1,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.main}1F 0%, ${theme.palette.secondary.main}1F 100%)`,
            color: 'primary.main',
            flexShrink: 0,
          }}
        >
          <ExploreOutlinedIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5">{t('provider.travelTitle')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('provider.travelSubtitle')}
          </Typography>
        </Box>
      </Stack>

      <Stack spacing={2} sx={{ mt: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={canTravel}
              onChange={(e) => setCanTravel(e.target.checked)}
            />
          }
          label={t('provider.travelToggle')}
        />

        {canTravel && (
          <TextField
            label={t('provider.travelRadiusLabel')}
            value={radius}
            onChange={(e) => setRadius(e.target.value.replace(/[^0-9]/g, ''))}
            inputProps={{ inputMode: 'numeric' }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {t('provider.travelRadiusUnit')}
                </InputAdornment>
              ),
            }}
            error={radius.trim() !== '' && !radiusValid}
            helperText={
              radius.trim() !== '' && !radiusValid
                ? t('provider.travelRadiusRange', { min: MIN_RADIUS, max: MAX_RADIUS })
                : t('provider.travelRadiusHint', {
                    city: cityName || t('provider.city'),
                  })
            }
            sx={{ maxWidth: 320 }}
          />
        )}

        <Box>
          <Button
            variant="contained"
            color="primary"
            disabled={save.isPending || isUnchanged || !radiusValid}
            onClick={() => save.mutate()}
          >
            {save.isPending ? t('common.loading') : t('common.save')}
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
}
